import { Css, Palette, SelectField } from "@homebound/beam";
import { useMemo } from "react";
import { type VersionableFragment } from "src/components/ReadyPlanVersionHeader.generated";
import { AggregateVersionStatus } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { formatDate } from "./FormattedDate";
import { Icon } from "./Icon";

type ReadyPlanVersionInfo = { aggregateVersions: VersionableFragment[] };

type ReadyPlanVersionHeaderProps = {
  readyPlan: ReadyPlanVersionInfo;
  versionId?: string;
  aggregateCheckoutId?: string;
  /** Defaults to `Version`, but can be set for things like `Previous Version`. */
  label?: string;
  /** Defaults to false, but can be true for `Previous Version` usages. */
  allowUnset?: boolean;
  /** Defaults to do a `navigateToRpav` on select, but can be overriden. */
  onSelect?: (versionId: string | undefined) => void;
  /**
   * Defaults to the compact, label-hidden treatment used in page headers. Set for a normal
   * labeled form field that fills its container, i.e. when the select is a form input rather
   * than page chrome.
   */
  inline?: boolean;
};

export function ReadyPlanVersionHeader(props: ReadyPlanVersionHeaderProps) {
  const {
    readyPlan,
    versionId,
    aggregateCheckoutId,
    label = "version",
    allowUnset = false,
    onSelect,
    inline = false,
  } = props;
  const navigateToRpav = useNavigateToRpav();
  const handleSelect = onSelect ?? navigateToRpav;

  const versionOptions = useMemo(
    () =>
      readyPlan.aggregateVersions
        ?.sortBy((v) => -(v.version ?? 0))
        .map((v) => ({
          id: v.id,
          name: v.status.code === AggregateVersionStatus.Draft ? `Draft of v${v.version}` : `v${v.version}`,
          promotedAt: v.promotedAt,
          promotedBy: v.promotedBy,
          aggregateCheckout: v.id === aggregateCheckoutId,
        })) ?? [],
    [readyPlan.aggregateVersions, aggregateCheckoutId],
  );

  return (
    <div css={Css.if(!inline).wPx(130).$}>
      <SelectField
        label={label}
        value={versionId}
        options={versionOptions}
        onSelect={handleSelect}
        getOptionLabel={(v) => v.name}
        getOptionValue={(v) => v.id}
        getOptionMenuLabel={(v) => (
          <div css={Css.df.fdc.$}>
            <div css={Css.df.gap1.$}>
              {v.name}{" "}
              {(v.promotedAt || v.aggregateCheckout) && (
                <Icon icon="cart" color={v.aggregateCheckout ? Palette.Blue500 : Palette.Gray500} />
              )}
            </div>
            {v.promotedAt && (
              <div css={Css.xs.gray700.$}>
                Promoted on {formatDate(v.promotedAt, "short")}
                {v.promotedBy && ` by ${v.promotedBy.name}`}
              </div>
            )}
          </div>
        )}
        fieldDecoration={(v) =>
          (v.promotedAt || v.aggregateCheckout) && (
            <Icon icon="cart" color={v.aggregateCheckout ? Palette.Blue500 : Palette.Gray500} />
          )
        }
        unsetLabel={allowUnset ? "-" : undefined}
        autoSort={false}
        labelStyle={inline ? "above" : "hidden"}
      />
    </div>
  );
}
